import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FieldValues, useFormContext } from "react-hook-form";
import { useQueryClient } from "react-query";
import { v4 as uuidv4 } from "uuid";
import { coursesProvider } from "../../providers/coursesProvider";
import { Row } from "../../misc/Row";
import { CreateBase, Form, SaveButton } from "react-admin";
import { InstructorInput } from "../../inputs/InstructorInput";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { renderAddress } from "../../misc/AddressDisplay";
import { SelectInput } from "../../inputs/SelectInput";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { TheoryCourseLesson } from "./TheoryCourseWizard";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { smallSelectInputStyle } from "../../misc/backofficeTheme";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";

export function CreateTheoryCourseForm(props: {
  initialLessons: Array<TheoryCourseLesson>;
  drivingSchool: DrivingSchool;
  goBack: () => void;
  onSuccess: () => void;
}) {
  const { initialLessons, drivingSchool, goBack, onSuccess } = props;
  const queryClient = useQueryClient();

  async function createCourse(data: FieldValues) {
    try {
      const courseId = uuidv4();
      const payload = data.lessons.map((lesson: TheoryCourseLesson) => ({
        courseUid: courseId,
        uid: lesson.id,
        type: "TheoryLesson",
        theoryLecture: lesson.theoryLecture,
        maxStudents: drivingSchool.branches.find((it) => it.uid === lesson.location)?.maxAmountOfStudents,
        location: drivingSchool.branches.find((it) => it.uid === lesson.location)?.postalAddress,
        attendees: {
          [lesson.instructorId]: {
            attendeeType: "instructor",
            rsvp: "accepted",
          },
        },
        start: lesson.start.toISO(),
        end: lesson.start.plus({ minutes: 90 }).toISO(),
      }));
      await coursesProvider.create("courses", { data: { theoryLessons: payload, drivingSchoolId: drivingSchool.id } });
      showSuccessToast("Theoriekurs wurde erfolgreich erstellt");
      void queryClient.invalidateQueries(["calendarEvents"]);
      void queryClient.invalidateQueries(["calendarEventHistory"]);
      void queryClient.invalidateQueries(["courses"]);
      onSuccess();
    } catch (error) {
      console.error("Failed to create theory course", error);
      showErrorToast("Der Theoriekurs konnte nicht erstellt werden");
    }
  }

  return (
    <CreateBase resource="courses">
      <Form onSubmit={createCourse} defaultValues={() => ({ lessons: initialLessons })}>
        <DialogContent>
          <Row mb={3}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Hier kannst du die Zeit, den Fahrlehrer oder die Filiale für die einzelnen Theorielektionen anpassen.
            </Typography>
          </Row>
          <Box sx={{ maxHeight: "600px", overflowY: "auto" }}>
            {initialLessons.map((lesson, index) => (
              <LessonAccordion key={lesson.id} index={index} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ pt: 2, justifyContent: "space-between" }}>
          <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={goBack}>
            Zurück
          </Button>
          <SaveButton icon={<></>} label="Kurs erstellen" alwaysEnable />
        </DialogActions>
      </Form>
    </CreateBase>
  );
}

function LessonAccordion(props: { index: number }) {
  const { index } = props;
  const { watch } = useFormContext();
  const lesson = watch(`lessons[${index}]`) as TheoryCourseLesson;
  const { branches } = useCurrentDrivingSchool();

  return (
    <Accordion
      key={lesson.id}
      square
      sx={{
        boxShadow: "none",
        margin: 0,
        "&.Mui-expanded": { margin: 0 },
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel-content-${index}`}
        id={`panel-header-${index}`}
        sx={{
          minHeight: "48px",
          "&.Mui-expanded": { minHeight: "48px" },
          "& .MuiAccordionSummary-content": { margin: "12px 0" },
          "& .MuiAccordionSummary-content.Mui-expanded": { margin: "12px 0" },
        }}
      >
        <Typography>
          {`Lektion ${lesson.label}: `}
          <Typography component="span" fontWeight="bold">
            {`${lesson.start.setLocale("de-DE").toFormat("ccc, dd.MM.yyyy HH:mm")} Uhr`}
          </Typography>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Row sx={{ marginTop: 1 }} gap={3} alignItems="center">
          <DateTimeInput
            allowPastDates
            source={`lessons[${index}].start`}
            sx={{ "& .MuiInputBase-input": { py: "8.5px" } }}
          />
          <InstructorInput
            source={`lessons[${index}].instructorId`}
            branchId={lesson.location}
            searchable
            sx={smallSelectInputStyle}
          />
          {branches.length > 1 && (
            <SelectInput
              source={`lessons[${index}].location`}
              label="Filiale"
              options={branches.map((it) => [
                it.uid,
                `${it.name} (${renderAddress(it.postalAddress, { oneLine: true })})`,
              ])}
              sx={{ ...smallSelectInputStyle, maxWidth: "300px" }}
              validate={(value) => (value ? true : "Bitte auswählen.")}
            />
          )}
        </Row>
      </AccordionDetails>
    </Accordion>
  );
}
